import React, { KeyboardEvent, MouseEvent } from "react";
import cn from "classnames";
import styles from "./gateway-announcement-status.module.scss";
import {
  AnnouncementStatus,
  GatewayDetails,
  GatewayOperationError,
  GatewayOnboardingStatus,
  getGatewayOperationProtocolEntries,
} from "@src/types/gateway-onboarding";
import SpinnerWithVariant from "@src/components/atoms/SpinnerWithVariant/spinner-with-variant";
import saveGreenRightIcon from "@src/assets/svg/save-green-right-icon.svg";
import closeRedIcon from "@src/assets/svg/close-red-icon.svg";
import WarningTriangleRedIcon from "@src/assets/svg/warning-triangle-red.svg";
import { Button } from "@src/components/atoms/buttons/button/button";
import { FlexRowContainer } from "@src/components/templates/layouts/flex-row-container/flex-row-container";
import { useI18N, useLanguage } from "@src/language/language-context";
import { GatewayDetailsDropdown } from "../gateway-details-dropdown/gateway-details-dropdown";
import { OperationProtocolDetails } from "@src/components/atoms/operation-protocol-details/operation-protocol-details";
import { getOpcUaErrorDescriptions } from "@src/cloud-connect/utils/opcua-status-codes";

const cssClassName = "gateway-announcement-status";

interface GatewayAnnouncementStatusProps {
  announcementStatus: AnnouncementStatus;
  gatewayOnboardingStatus: GatewayOnboardingStatus;
  gatewayDetails?: GatewayDetails;
  failureReason?: string;
  failureDetails?: GatewayOperationError | null;
  showProtocolDetails?: boolean;
  isGatewayAnnounced?: boolean;
  isGatewayAlreadyOnboarded?: boolean;
  onRetry?: () => void;
  onOnboardAnotherGateway?: () => void;
  onShowProtocol?: () => void;
  onGetSupport?: () => void;
}

export const GatewayAnnouncementStatus = ({
  announcementStatus,
  gatewayOnboardingStatus,
  gatewayDetails,
  failureReason,
  failureDetails,
  showProtocolDetails,
  isGatewayAnnounced,
  isGatewayAlreadyOnboarded,
  onRetry,
  onOnboardAnotherGateway,
  onShowProtocol,
  onGetSupport,
}: GatewayAnnouncementStatusProps) => {
  const i18n = useI18N();
  const language = useLanguage();

  const texts = {
    errorLabel: i18n("modules.addNewGatewayDrawerModal.errorLabel"),
    announcingGateway: i18n(
      "modules.addNewGatewayDrawerModal.announcingGateway",
    ),
    onboardingGateway: i18n(
      "modules.addNewGatewayDrawerModal.onboardingGateway",
    ),
    announcedSuccess: i18n("modules.addNewGatewayDrawerModal.announcedSuccess"),
    onboardedSuccess: i18n("modules.addNewGatewayDrawerModal.onboardedSuccess"),
    announceFailed: i18n("modules.addNewGatewayDrawerModal.announceFailed"),
    onboardFailed: i18n("modules.addNewGatewayDrawerModal.onboardFailed"),
    gatewayAlreadyOnboarded: i18n(
      "modules.addNewGatewayDrawerModal.gatewayAlreadyOnboarded",
    ),
    connectionFailed: i18n("modules.addNewGatewayDrawerModal.connectionFailed"),
    operationPendingWarningMessage: i18n(
      "modules.addNewGatewayDrawerModal.operationPendingWarningMessage",
    ),
    retry: i18n("modules.addNewGatewayDrawerModal.retry"),
    onboardAnotherGateway: i18n(
      "modules.addNewGatewayDrawerModal.onboardAnotherGateway",
    ),
    showProtocol: i18n("modules.addNewGatewayDrawerModal.showProtocol"),
    hideProtocol: i18n("modules.addNewGatewayDrawerModal.hideProtocol"),
    getSupport: i18n("modules.addNewGatewayDrawerModal.getSupport"),
    statusIconAltText: i18n(
      "modules.addNewGatewayDrawerModal.statusIconAltText",
    ),
    opcUaErrorDetailsTitle: i18n(
      "modules.addNewMachineDrawerModal.opcUaErrorDetailsTitle",
    ),
  };

  if (announcementStatus === AnnouncementStatus.IDLE) {
    return null;
  }

  const renderStatusRow = (
    icon: React.ReactNode,
    text: string,
    textVariant?: "success" | "error" | "loading" | "warning",
    wrapText = false,
  ) => (
    <div className={styles[`${cssClassName}__status-row`]}>
      <span className={styles[`${cssClassName}__status-icon`]}>{icon}</span>
      <span
        className={cn(styles[`${cssClassName}__status-text`], {
          [styles[`${cssClassName}__status-text--success`]]:
            textVariant === "success",
          [styles[`${cssClassName}__status-text--error`]]:
            textVariant === "error",
          [styles[`${cssClassName}__status-text--warning`]]:
            textVariant === "warning",
          [styles[`${cssClassName}__status-text--loading`]]:
            textVariant === "loading",
          [styles[`${cssClassName}__status-text--multiline`]]: wrapText,
        })}
        title={text}
      >
        {text}
      </span>
    </div>
  );

  const handleFooterButtonClick =
    (handler?: () => void) =>
    (e: MouseEvent | KeyboardEvent): void => {
      e.preventDefault();
      handler?.();
    };

  if (announcementStatus === AnnouncementStatus.LOADING) {
    return (
      <div
        className={cn(styles[cssClassName], styles[`${cssClassName}--loading`])}
      >
        {renderStatusRow(
          <SpinnerWithVariant variant={["small", "secondary"]} />,
          texts.announcingGateway,
          "loading",
        )}
      </div>
    );
  }

  if (announcementStatus === AnnouncementStatus.ANNOUNCED) {
    return (
      <div
        className={cn(styles[cssClassName], styles[`${cssClassName}--loading`])}
      >
        {renderStatusRow(
          <img src={saveGreenRightIcon} alt={texts.statusIconAltText} />,
          texts.announcedSuccess,
          "success",
        )}
        {(gatewayOnboardingStatus ===
          GatewayOnboardingStatus.GATEWAY_ONBOARDING_ANNOUNCED ||
          gatewayOnboardingStatus ===
            GatewayOnboardingStatus.GATEWAY_ONBOARDING_IN_PROGRESS) &&
          renderStatusRow(
            <SpinnerWithVariant variant={["small", "secondary"]} />,
            texts.onboardingGateway,
            "loading",
          )}
      </div>
    );
  }

  if (announcementStatus === AnnouncementStatus.SUCCESS) {
    return (
      <div
        className={cn(styles[cssClassName], styles[`${cssClassName}--success`])}
      >
        {renderStatusRow(
          <img src={saveGreenRightIcon} alt={texts.statusIconAltText} />,
          texts.announcedSuccess,
          "success",
        )}
        {renderStatusRow(
          <img src={saveGreenRightIcon} alt={texts.statusIconAltText} />,
          texts.onboardedSuccess,
          "success",
        )}
        {gatewayDetails && (
          <GatewayDetailsDropdown gatewayDetails={gatewayDetails} />
        )}
      </div>
    );
  }

  if (announcementStatus === AnnouncementStatus.WARNING) {
    return (
      <div
        className={cn(styles[cssClassName], styles[`${cssClassName}--warning`])}
      >
        {isGatewayAnnounced &&
          renderStatusRow(
            <img src={saveGreenRightIcon} alt={texts.statusIconAltText} />,
            texts.announcedSuccess,
            "success",
          )}
        {renderStatusRow(null, texts.operationPendingWarningMessage, "warning")}
        {gatewayDetails?.vendor_id != null && gatewayDetails.gateway_id && (
          <GatewayDetailsDropdown gatewayDetails={gatewayDetails} />
        )}
        {onOnboardAnotherGateway && (
          <Button
            fun={handleFooterButtonClick(onOnboardAnotherGateway)}
            text={texts.onboardAnotherGateway}
            variant="secondary"
          />
        )}
      </div>
    );
  }

  if (announcementStatus === AnnouncementStatus.FAILED) {
    if (isGatewayAlreadyOnboarded) {
      return (
        <div
          className={cn(
            styles[cssClassName],
            styles[`${cssClassName}--warning`],
          )}
        >
          {renderStatusRow(
            null,
            texts.gatewayAlreadyOnboarded,
            "warning",
            true,
          )}
          {onOnboardAnotherGateway && (
            <Button
              fun={handleFooterButtonClick(onOnboardAnotherGateway)}
              text={texts.onboardAnotherGateway}
              variant={["secondary", "full-width"]}
            />
          )}
        </div>
      );
    }

    const resolvedFailureMessage = failureReason?.trim()
      ? `${texts.connectionFailed}: ${failureReason.trim()}`
      : texts.connectionFailed;

    const protocolEntries = getGatewayOperationProtocolEntries(failureDetails);
    const opcUaErrorDescriptions = getOpcUaErrorDescriptions(
      failureDetails?.additional_error_codes,
      language,
    );
    const hasProtocolDetails =
      protocolEntries.length > 0 || opcUaErrorDescriptions.length > 0;

    return (
      <div className={styles[`${cssClassName}__error-wrapper`]}>
        <p className={styles[`${cssClassName}__error-label`]}>
          {texts.errorLabel}
        </p>
        <div
          className={cn(styles[cssClassName], styles[`${cssClassName}--error`])}
        >
          {isGatewayAnnounced
            ? renderStatusRow(
                <img src={saveGreenRightIcon} alt={texts.statusIconAltText} />,
                texts.announcedSuccess,
                "success",
              )
            : renderStatusRow(
                <img src={closeRedIcon} alt={texts.statusIconAltText} />,
                texts.announceFailed,
                "error",
              )}
          {isGatewayAnnounced &&
            renderStatusRow(
              <img src={closeRedIcon} alt={texts.statusIconAltText} />,
              texts.onboardFailed,
              "error",
            )}
          <FlexRowContainer horizontalGap={8} justifyContent="space-between">
            <div className={styles[`${cssClassName}__status-row`]}>
              <span className={styles[`${cssClassName}__status-icon`]}>
                <img
                  src={WarningTriangleRedIcon}
                  alt={texts.statusIconAltText}
                />
              </span>
              <span
                className={cn(
                  styles[`${cssClassName}__status-text`],
                  styles[`${cssClassName}__status-text--error`],
                  styles[`${cssClassName}__status-text--connection-failed`],
                  {
                    [styles[
                      `${cssClassName}__status-text--connection-failed-expanded`
                    ]]: showProtocolDetails,
                  },
                )}
                title={resolvedFailureMessage}
              >
                {resolvedFailureMessage}
              </span>
            </div>
            {onRetry && (
              <Button
                fun={handleFooterButtonClick(onRetry)}
                text={texts.retry}
                variant="secondary"
              />
            )}
          </FlexRowContainer>
          <FlexRowContainer horizontalGap={8} justifyContent="space-around">
            {onShowProtocol && hasProtocolDetails && (
              <Button
                fun={handleFooterButtonClick(onShowProtocol)}
                text={
                  showProtocolDetails ? texts.hideProtocol : texts.showProtocol
                }
                variant={["no-outline-with-green20-hover", "color-blue"]}
              />
            )}
            {onGetSupport && (
              <Button
                fun={handleFooterButtonClick(onGetSupport)}
                text={texts.getSupport}
                variant={["no-outline-with-green20-hover", "color-blue"]}
              />
            )}
          </FlexRowContainer>
          {showProtocolDetails && hasProtocolDetails && (
            <OperationProtocolDetails
              className={styles[`${cssClassName}__protocol-details`]}
              protocolEntries={protocolEntries}
              opcUaErrorDescriptions={opcUaErrorDescriptions}
              opcUaErrorDetailsTitle={texts.opcUaErrorDetailsTitle}
            />
          )}
        </div>
      </div>
    );
  }

  return null;
};
